import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { getDashboard, getUnreadCount } from '../utils/api';
import { formatCurrency, formatDate, caseTypeLabel, timeAgo } from '../utils/helpers';
import {
  Briefcase, Users, DollarSign, AlertTriangle,
  ArrowRight, Clock, TrendingUp, UserPlus, CheckCircle2,
  MessageCircle, Calendar, AlertCircle, ChevronRight, Bell,
} from 'lucide-react';

const REFRESH_INTERVAL = 30000; // auto-refresh every 30s

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadMsgs, setUnreadMsgs] = useState(0);

  const loadDashboard = useCallback(async () => {
    try {
      const result = await getDashboard();
      setData(result);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUnread = useCallback(async () => {
    try {
      const result = await getUnreadCount();
      setUnreadMsgs(result?.unread || 0);
    } catch {}
  }, []);

  useEffect(() => {
    loadDashboard();
    loadUnread();
    const iv = setInterval(() => { loadDashboard(); loadUnread(); }, REFRESH_INTERVAL);
    return () => clearInterval(iv);
  }, [loadDashboard, loadUnread]);

  if (loading) return <div className="loading-spinner"><div className="spinner" /></div>;

  // New clients created in last 7 days
  const newClients = data?.new_clients_count ?? data?.clients?.new_this_week ?? 0;
  // My tasks = tasks assigned to current user
  const myTasks = data?.my_tasks || [];
  const myOverdueTasks = myTasks.filter(t => t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed');
  const myPendingTasks = myTasks.filter(t => t.status !== 'completed');

  return (
    <>
      <Header title="Dashboard">
        {/* Quick actions */}
        <Link to="/messages" className="btn btn-ghost btn-sm" style={{ position: 'relative' }}>
          <MessageCircle size={16} />
          {unreadMsgs > 0 && (
            <span style={{
              position: 'absolute', top: '-4px', right: '-4px',
              background: 'var(--status-red)', color: '#fff',
              borderRadius: '10px', padding: '1px 5px',
              fontSize: '0.65rem', fontWeight: 700, minWidth: '16px', textAlign: 'center',
            }}>
              {unreadMsgs > 99 ? '99+' : unreadMsgs}
            </span>
          )}
        </Link>
      </Header>

      <div className="app-content">
        {/* ── Welcome Banner ── */}
        <div style={{
          padding: '20px 24px', marginBottom: '20px',
          background: 'var(--zeus-gold-dim)', borderRadius: 'var(--radius-md)',
          border: '1px solid rgba(212,168,83,0.15)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 400, margin: 0 }}>
              Welcome back, <span style={{ color: 'var(--zeus-gold)' }}>{user?.first_name || 'User'}</span>
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', margin: '4px 0 0' }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
        </div>

        {/* ── Action Required — Alerts Panel ── */}
        {data?.alerts?.length > 0 && (
          <div className="card" style={{ marginBottom: '20px', borderLeft: '3px solid #ef4444' }}>
            <div className="card-header">
              <h3 className="card-title">
                <Bell size={16} style={{ marginRight: '6px', color: '#ef4444' }} />
                Action Required
                <span style={{
                  marginLeft: '8px', fontSize: '0.72rem', padding: '2px 8px',
                  borderRadius: '10px', background: 'rgba(239,68,68,0.12)', color: '#ef4444', fontWeight: 700,
                }}>
                  {data.alerts.length}
                </span>
              </h3>
              <div style={{ display: 'flex', gap: '6px', fontSize: '0.72rem' }}>
                {(() => {
                  const counts = {};
                  data.alerts.forEach(a => { counts[a.type] = (counts[a.type] || 0) + 1; });
                  const typeLabels = { sol: 'SOL', overdue_task: 'Overdue Tasks', stale: 'Stale Cases', treatment_gap: 'Treatment Gaps' };
                  const typeColors = { sol: '#ef4444', overdue_task: '#f97316', stale: '#f59e0b', treatment_gap: '#3b82f6' };
                  return Object.entries(counts).map(([type, count]) => (
                    <span key={type} style={{
                      padding: '2px 8px', borderRadius: '8px',
                      background: `${typeColors[type] || '#64748b'}15`,
                      color: typeColors[type] || '#64748b', fontWeight: 600,
                    }}>
                      {count} {typeLabels[type] || type}
                    </span>
                  ));
                })()}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '320px', overflowY: 'auto' }}>
              {data.alerts.slice(0, 20).map((alert, i) => {
                const severityConfig = {
                  critical: { color: '#ef4444', bg: 'rgba(239,68,68,0.06)', icon: AlertTriangle },
                  warning:  { color: '#f97316', bg: 'rgba(249,115,22,0.04)', icon: AlertCircle },
                  info:     { color: '#3b82f6', bg: 'rgba(59,130,246,0.04)', icon: Clock },
                };
                const sev = severityConfig[alert.severity] || severityConfig.info;
                const IconComp = sev.icon;
                const typeIcons = { sol: Clock, overdue_task: CheckCircle2, stale: AlertCircle, treatment_gap: Clock };
                const TypeIcon = typeIcons[alert.type] || AlertCircle;
                const typeLabels = { sol: 'SOL', overdue_task: 'Task', stale: 'Stale', treatment_gap: 'Gap' };

                return (
                  <div
                    key={`${alert.type}-${alert.case_id}-${i}`}
                    onClick={() => {
                      if (alert.case_id) navigate(`/cases/${alert.case_id}`);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 16px', cursor: alert.case_id ? 'pointer' : 'default',
                      background: sev.bg, transition: 'background 0.15s',
                      borderLeft: `3px solid ${sev.color}`,
                    }}
                    onMouseOver={e => { if (alert.case_id) e.currentTarget.style.background = `${sev.color}12`; }}
                    onMouseOut={e => e.currentTarget.style.background = sev.bg}
                  >
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                      background: `${sev.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <IconComp size={14} style={{ color: sev.color }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '1px' }}>
                        {alert.case_number && (
                          <span style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--zeus-gold)' }}>{alert.case_number}</span>
                        )}
                        {alert.client_name && (
                          <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{alert.client_name}</span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {alert.message}
                      </div>
                    </div>
                    <span style={{
                      fontSize: '0.62rem', fontWeight: 700, padding: '2px 6px', borderRadius: '6px',
                      background: `${sev.color}15`, color: sev.color, flexShrink: 0,
                    }}>
                      {typeLabels[alert.type] || alert.type}
                    </span>
                    {alert.case_id && <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                  </div>
                );
              })}
            </div>
            {data.alerts.length > 20 && (
              <div style={{ padding: '8px 16px', fontSize: '0.75rem', color: 'var(--text-muted)', borderTop: '1px solid var(--zeus-border)', textAlign: 'center' }}>
                Showing 20 of {data.alerts.length} alerts
              </div>
            )}
          </div>
        )}

        {/* ── Main Stats Grid ── */}
        <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
          {/* New Clients - LIVE */}
          <div className="stat-card" style={{ borderLeft: '3px solid var(--status-green)' }}>
            <span className="stat-label">
              <UserPlus size={14} style={{ marginRight: '4px', color: 'var(--status-green)' }} />
              New Clients (7d)
            </span>
            <span className="stat-value" style={{ color: 'var(--status-green)' }}>{newClients}</span>
            <span className="stat-change positive">
              <TrendingUp size={14} />
              This week
            </span>
          </div>

          {/* Active Cases */}
          <div className="stat-card">
            <span className="stat-label">Active Cases</span>
            <span className="stat-value">{data?.cases?.total || 0}</span>
            <span className="stat-change positive">
              <TrendingUp size={14} />
              {data?.cases?.by_type?.personal_injury || 0} PI · {data?.cases?.by_type?.workers_compensation || 0} WC
              {data?.cases?.by_type?.department_of_labor ? ` · ${data.cases.by_type.department_of_labor} DOL` : ''}
            </span>
          </div>

          {/* Total Clients */}
          <div className="stat-card">
            <span className="stat-label">Total Clients</span>
            <span className="stat-value">{data?.clients?.total || 0}</span>
          </div>

          {/* Revenue */}
          <div className="stat-card">
            <span className="stat-label">Revenue</span>
            <span className="stat-value">{formatCurrency(data?.billing?.total_paid)}</span>
            <span className="stat-change positive">
              <DollarSign size={14} />
              {formatCurrency(data?.billing?.total_billed)} billed
            </span>
          </div>

          {/* Outstanding */}
          <div className="stat-card">
            <span className="stat-label">Outstanding</span>
            <span className="stat-value">{formatCurrency(data?.billing?.total_outstanding)}</span>
            <span className="stat-change negative">
              <AlertTriangle size={14} />
              {data?.billing?.invoice_count || 0} invoices
            </span>
          </div>
        </div>

        <div className="grid-2">
          {/* ── My Tasks (User-specific) ── */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <CheckCircle2 size={16} style={{ marginRight: '6px', color: 'var(--zeus-gold)' }} />
                My Tasks
                {myPendingTasks.length > 0 && (
                  <span style={{
                    marginLeft: '8px', fontSize: '0.75rem', padding: '2px 8px',
                    borderRadius: '10px', background: 'var(--zeus-gold-dim)', color: 'var(--zeus-gold)',
                  }}>
                    {myPendingTasks.length} pending
                  </span>
                )}
              </h3>
              <Link to="/tasks" className="btn btn-ghost btn-sm">
                All Tasks <ArrowRight size={14} />
              </Link>
            </div>

            {/* Overdue tasks first */}
            {myOverdueTasks.length > 0 && (
              <div style={{
                padding: '10px 14px', marginBottom: '10px',
                background: 'rgba(239,68,68,0.08)', borderRadius: 'var(--radius-sm)',
                border: '1px solid rgba(239,68,68,0.2)',
              }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--status-red)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  Overdue ({myOverdueTasks.length})
                </div>
                {myOverdueTasks.slice(0, 3).map(task => (
                  <div key={task.id || task._id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '6px 0', fontSize: '0.85rem',
                  }}>
                    <span style={{ color: 'var(--text-primary)' }}>{task.title}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--status-red)' }}>
                      {formatDate(task.due_date)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {myPendingTasks.filter(t => !myOverdueTasks.includes(t)).length > 0 ? (
              <div>
                {myPendingTasks
                  .filter(t => !myOverdueTasks.includes(t))
                  .slice(0, 5)
                  .map(task => (
                    <div key={task.id || task._id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 0', borderBottom: '1px solid var(--zeus-border)',
                    }}>
                      <div>
                        <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)' }}>{task.title}</div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                          <span className={`badge badge-${task.priority}`} style={{ fontSize: '0.68rem' }}>
                            {task.priority}
                          </span>
                          {task.due_date && (
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                              Due {formatDate(task.due_date)}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="badge badge-medium" style={{ fontSize: '0.68rem' }}>
                        {(task.status || 'todo').replace('_', ' ')}
                      </span>
                    </div>
                  ))}
              </div>
            ) : myOverdueTasks.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', padding: '16px 0' }}>
                No tasks assigned to you — great work!
              </p>
            ) : null}
          </div>

          {/* ── Right Column: Events + Recent Cases ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Upcoming Events */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  <Calendar size={16} style={{ marginRight: '6px', color: 'var(--zeus-gold)' }} />
                  Upcoming Events
                </h3>
                <Link to="/calendar" className="btn btn-ghost btn-sm">
                  Calendar <ArrowRight size={14} />
                </Link>
              </div>
              {data?.upcoming_events?.length > 0 ? (
                <div>
                  {data.upcoming_events.slice(0, 5).map(evt => {
                    const evtColors = {
                      treatment: '#0d9488', hearing: '#3b82f6', deadline: '#ef4444',
                      mediation: '#f59e0b', sol: '#a855f7', general: '#64748b',
                    };
                    const dotColor = evtColors[evt.event_type] || evtColors.general;
                    return (
                      <div
                        key={evt.id}
                        onClick={() => navigate('/calendar', { state: { openEventId: evt.id } })}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '12px',
                          padding: '10px 0', borderBottom: '1px solid var(--zeus-border)',
                          cursor: 'pointer', borderRadius: 6, transition: 'background 0.12s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--zeus-navy-light)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: dotColor, flexShrink: 0, marginLeft: 4,
                        }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)' }}>{evt.title}</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                            {formatDate(evt.start_time)}{evt.case?.case_number ? ` · ${evt.case.case_number}` : ''}
                          </div>
                        </div>
                        {evt.event_type && (
                          <span style={{
                            fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px',
                            borderRadius: 10, background: `${dotColor}22`, color: dotColor,
                          }}>{evt.event_type}</span>
                        )}
                        <ArrowRight size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', padding: '16px 0' }}>
                  No upcoming events this week.
                </p>
              )}
            </div>

            {/* Messages Quick Glance */}
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  <MessageCircle size={16} style={{ marginRight: '6px', color: 'var(--zeus-gold)' }} />
                  Messages
                  {unreadMsgs > 0 && (
                    <span style={{
                      marginLeft: '8px', background: 'var(--status-red)', color: '#fff',
                      borderRadius: '10px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600,
                    }}>
                      {unreadMsgs} new
                    </span>
                  )}
                </h3>
                <Link to="/messages" className="btn btn-ghost btn-sm">
                  Open <ArrowRight size={14} />
                </Link>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem', padding: '8px 0' }}>
                {unreadMsgs > 0
                  ? `You have ${unreadMsgs} unread message${unreadMsgs !== 1 ? 's' : ''}. Click to view.`
                  : 'All caught up — no new messages.'}
              </p>
            </div>
          </div>
        </div>

        {/* ── Recent Cases ── */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3 className="card-title">Recent Cases</h3>
            <Link to="/cases" className="btn btn-ghost btn-sm">
              View All <ArrowRight size={14} />
            </Link>
          </div>
          {data?.recent_cases?.length > 0 ? (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Case #</th>
                    <th>Client</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_cases.slice(0, 8).map(c => (
                    <tr key={c.id || c._id}>
                      <td>
                        <Link to={`/cases/${c.id || c._id}`} style={{ color: 'var(--zeus-gold)', textDecoration: 'none', fontWeight: 500 }}>
                          {c.case_number}
                        </Link>
                      </td>
                      <td style={{ color: 'var(--text-primary)' }}>{c.client_name}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{c.claim_type || caseTypeLabel(c.case_type)}</td>
                      <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{formatDate(c.created_at || c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <Briefcase />
              <h3>No cases yet</h3>
              <p>Create your first case to get started.</p>
            </div>
          )}
        </div>

        {/* ── Stale Cases ── */}
        {data?.stale_cases?.length > 0 && (
          <div className="card" style={{ marginTop: '20px', borderLeft: '3px solid #f97316' }}>
            <div className="card-header">
              <h3 className="card-title">
                <AlertCircle size={16} style={{ marginRight: '6px', color: '#f97316' }} />
                Stale Cases
                <span style={{
                  marginLeft: '8px', fontSize: '0.75rem', padding: '2px 8px',
                  borderRadius: '10px', background: 'rgba(249,115,22,0.12)', color: '#f97316', fontWeight: 700,
                }}>
                  {data.stale_cases.length} case{data.stale_cases.length !== 1 ? 's' : ''}
                </span>
              </h3>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                No activity in {data.stale_threshold_days || 30}+ days
              </span>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Case #</th>
                    <th>Client</th>
                    <th>Attorney</th>
                    <th>Status</th>
                    <th>Days Stale</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stale_cases.slice(0, 15).map(c => {
                    const severity = c.days_stale >= 60 ? '#ef4444' : c.days_stale >= 45 ? '#f97316' : '#f59e0b';
                    return (
                      <tr key={c.id}>
                        <td>
                          <Link to={`/cases/${c.id}`} style={{ color: 'var(--zeus-gold)', textDecoration: 'none', fontWeight: 500 }}>
                            {c.case_number}
                          </Link>
                        </td>
                        <td style={{ color: 'var(--text-primary)' }}>{c.client_name}</td>
                        <td style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{c.assigned_attorney || '--'}</td>
                        <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                        <td>
                          <span style={{
                            fontWeight: 700, fontSize: '0.85rem', color: severity,
                          }}>
                            {c.days_stale}d
                          </span>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{c.last_activity_type}</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                            {c.last_activity_date ? new Date(c.last_activity_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '--'}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {data.stale_cases.length > 15 && (
              <div style={{ padding: '10px 16px', fontSize: '0.78rem', color: 'var(--text-muted)', borderTop: '1px solid var(--zeus-border)' }}>
                Showing 15 of {data.stale_cases.length} stale cases. Review all cases in the Cases page.
              </div>
            )}
          </div>
        )}

      </div>
    </>
  );
}
